import { cloneDeep, sum } from 'lodash';
import { render, unmount } from 'rc-util/lib/React/render';
import { Tooltip } from '@meitu/saas-ui';
import IconFont from '@meitu/xdesign-icon-font';
import { getFramework } from '@/framework';
import { getLocaleStore } from '@/i18n';
import { getRowOrColActions } from '@/layers/table-layer/actions/row-col-actions';
import { SIDE_BAR_BOLD } from '@/layers/table-layer/default-configs';
import { getCellStyle } from '@/layers/table-layer/utils/table';
import { getLayerInfoWithRotate } from '@/utils/layer-kit';
import { ITableLayer, RowOrColSelectedRectInfo } from '@/version-types';
import addOrUpdateLayer from '../utils/add-or-update-layer';
import './selected-row-or-col-shortcut.less';

interface IRowOrColShortcut {
    iconName: string;
    title: string;
    onClick: () => void;
}

const RowOrColShortcut: React.FC<IRowOrColShortcut> = ({ iconName, title, onClick }) => (
    <div onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
        <Tooltip placement="top" title={title}>
            <div className="row-or-col-shortcut" onClick={onClick}>
                <IconFont fontSize={16} name={iconName} />
            </div>
        </Tooltip>
    </div>
);

export const registerSelectedRowOrColShortcut = ({
    layerDom,
    frameworkIdentity,
    layerId,
    rowOrColSelectedRectInfo: { direction, index },
}: {
    /** 用于做相对定位的dom */
    layerDom: HTMLElement;
    frameworkIdentity: string;
    layerId: string;
    rowOrColSelectedRectInfo: RowOrColSelectedRectInfo;
}) => {
    const { $t } = getLocaleStore();
    const framework = getFramework(frameworkIdentity);

    const getShortcutInfo = () => {
        const layer = (framework.getLayer(layerId) ?? null) as ITableLayer | null;

        if (!layer) return null;

        const { horizontal: horizontalSizes, vertical: verticalSizes } = layer.data.baseCells;

        // 只剩一行或者一列时, 不可删除
        const isDisabled =
            index === 0 &&
            ((direction === 'horizontal' && verticalSizes.length === 1) ||
                (direction === 'vertical' && horizontalSizes.length === 1));
        if (isDisabled) return null;

        const shortcut: IRowOrColShortcut = {
            iconName: 'TrashCanBold',
            title: $t((v) => v.tableLayer.delete),
            onClick: () => {
                const layer = (framework.getLayer(layerId) ?? null) as ITableLayer | null;
                if (!layer) return;

                const { delRow, delCol } = getRowOrColActions(frameworkIdentity);

                const action = direction === 'horizontal' ? delRow : delCol;
                let newTableLayer = action({
                    tableLayer: layer,
                    targetIndex: index,
                });
                if (newTableLayer) {
                    newTableLayer = addOrUpdateLayer({
                        frameworkIdentity,
                        layer: newTableLayer,
                        resetMaterialIds: true,
                    });
                    framework.operate.setActiveLayer(newTableLayer.id);
                    framework.history.write(cloneDeep(layer), newTableLayer);
                }
            },
        };

        const getPosition = () => {
            const firstCellStyle = getCellStyle(layer.data.cells[0][0]!, layer.data);
            const tableFakeBorderTop = (firstCellStyle.border?.top?.width ?? 0) / 2;
            const tableFakeBorderLeft = (firstCellStyle.border?.left?.width ?? 0) / 2;
            const tableFakeBorderRight = layer.width - tableFakeBorderLeft - sum(horizontalSizes);
            const tableFakeBorderBottom = layer.height - tableFakeBorderTop - sum(verticalSizes);
            const tableFakeBorder = {
                top: tableFakeBorderTop,
                left: tableFakeBorderLeft,
                right: tableFakeBorderRight,
                bottom: tableFakeBorderBottom,
            };

            const shortcutOperateSize = 34;
            const scale = framework.stage.getScale();
            const sizes = direction === 'horizontal' ? verticalSizes : horizontalSizes;
            const displayTopDiff = 14;
            const displayLeftDiff = 14;
            const isFirst = index === 0;
            const isLast = index === sizes.length - 1;
            const position =
                direction === 'horizontal'
                    ? {
                          top: isFirst
                              ? tableFakeBorder.top / 2 + sizes[0] / 2
                              : isLast
                                ? tableFakeBorder.top +
                                  sum(sizes.slice(0, index)) +
                                  sizes[index] / 2 +
                                  tableFakeBorder.bottom / 2
                                : tableFakeBorder.top + sum(sizes.slice(0, index)) + sizes[index] / 2,
                          left: (-1 * (shortcutOperateSize / 2 + SIDE_BAR_BOLD + displayLeftDiff)) / scale,
                      }
                    : {
                          top: (-1 * (shortcutOperateSize / 2 + SIDE_BAR_BOLD + displayTopDiff)) / scale,
                          left: isFirst
                              ? tableFakeBorder.left / 2 + sizes[0] / 2
                              : isLast
                                ? tableFakeBorder.left +
                                  sum(sizes.slice(0, index)) +
                                  sizes[index] / 2 +
                                  tableFakeBorder.right / 2
                                : tableFakeBorder.left + sum(sizes.slice(0, index)) + sizes[index] / 2,
                      };
            // 如果快捷操作框超出顶部，则将快捷操作框移动到图层下方
            if (direction === 'vertical' && layerDom.getBoundingClientRect().top < 120) {
                const offset = (layer.rotate <= 10 && layer.rotate >= 0) || layer.rotate >= 350 ? 26 : 0;
                const info = getLayerInfoWithRotate(layer);
                console.log('===rect', offset, info);
                position.top = info.top + info.height + 10 + offset;
            }
            return position;
        };

        return {
            shortcut,
            position: getPosition(),
        };
    };

    const containerId = 'row-or-col-shortcut-container';

    const registerCustomShortcut = () => {
        framework.operate.shortcutMenu.hide(true);
        const div = document.createElement('div');
        layerDom.appendChild(div);
        div.id = containerId;
        const shortcutData = getShortcutInfo();
        if (!shortcutData) return;
        const { shortcut, position } = shortcutData;
        div.style.position = 'absolute';
        div.style.top = `${position.top}px`;
        div.style.left = `${position.left}px`;
        div.style.transform = `scale(${1 / framework.stage.getScale()}) translate(-50%, -50%)`;
        div.style.transformOrigin = 'top left';
        div.style.zIndex = '10000';
        render(
            <RowOrColShortcut iconName={shortcut.iconName} title={shortcut.title} onClick={shortcut.onClick} />,
            div,
        );
    };
    const unregisterCustomShortcut = () => {
        framework.operate.shortcutMenu.hide(false);
        const el = layerDom.querySelector(`#${containerId}`);
        if (el && el.parentNode) {
            unmount(el);
            el.parentNode.removeChild(el);
        }
    };

    // registerCustomShortcut();
    // return unregisterCustomShortcut;
    return () => {};
};
